import { useEffect, useMemo, useRef, useState, type RefObject } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from "@mui/material";
import type {
  ListeningAssessmentQuestion,
  ListeningStepComponentProps,
} from "./types";
import { normalizeListeningQuestionType } from "../utils";
import { RadioButtonsGroup } from "../../../../components/RadioButtonsGroup";
import { usePreloadAudioUrls } from "../../hooks/usePreloadAudioUrls";
import { InstructionAudioButton } from "../../../../components/InstructionAudioButton";

const extractOptions = (
  question: ListeningAssessmentQuestion,
): { a: string; b: string } => {
  const fromQuestion = {
    a: question.optionA ?? "",
    b: question.optionB ?? "",
  };

  if (fromQuestion.a || fromQuestion.b) {
    return {
      a: fromQuestion.a || "Yes",
      b: fromQuestion.b || "No",
    };
  }

  const stringOptions = (question.options ?? []).filter(
    (option): option is string => typeof option === "string",
  );

  return {
    a: stringOptions[0] ?? "Yes",
    b: stringOptions[1] ?? "No",
  };
};

const AudioPlayer = ({
  src,
  audioRef,
  isPreview = false,
}: {
  src: string;
  audioRef?: RefObject<HTMLAudioElement | null>;
  isPreview?: boolean;
}) => {
  if (!src) {
    return null;
  }
  return (
    <audio
      ref={audioRef}
      controls
      controlsList={
        isPreview ? undefined : "nodownload noplaybackrate noremoteplayback"
      }
      preload={isPreview ? undefined : "auto"}
      crossOrigin={isPreview ? undefined : "anonymous"}
      src={src}
      style={{ width: "100%" }}
    />
  );
};

export function YesNoQuestion({
  assessment,
  isLoading,
  isPreview = false,
  onStepReadyChange,
  onSaveAnswer,
}: ListeningStepComponentProps) {
  const yesNoQuestions = useMemo(() => {
    const partQuestions = assessment?.part?.questions ?? [];
    return partQuestions
      .filter(
        (question) =>
          normalizeListeningQuestionType(question.question_type) === "yes_no",
      )
      .sort((a, b) => a.question_order - b.question_order);
  }, [assessment?.part?.questions]);

  const initialAnswerByQuestionId = useMemo(
    () =>
      Object.fromEntries(
        yesNoQuestions
          .map((question) => {
            const savedAnswer =
              assessment?.submissionProgress?.answers_by_question_id?.[
                question.question_id
              ];
            if (savedAnswer !== "A" && savedAnswer !== "B") {
              return null;
            }
            return [question.question_id, savedAnswer] as const;
          })
          .filter((entry): entry is readonly [string, "A" | "B"] =>
            Boolean(entry),
          ),
      ),
    [assessment?.submissionProgress?.answers_by_question_id, yesNoQuestions],
  );
  const initialActiveQuestionIndex = useMemo(() => {
    const firstUnansweredIndex = yesNoQuestions.findIndex(
      (question) =>
        !assessment?.submissionProgress?.answers_by_question_id?.[
          question.question_id
        ],
    );
    return firstUnansweredIndex >= 0 ? firstUnansweredIndex : 0;
  }, [assessment?.submissionProgress?.answers_by_question_id, yesNoQuestions]);
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(
    initialActiveQuestionIndex,
  );
  const [answerByQuestionId, setAnswerByQuestionId] = useState<
    Record<string, "A" | "B">
  >(() => initialAnswerByQuestionId);
  const promptAudioRef = useRef<HTMLAudioElement | null>(null);

  const safeQuestionIndex = Math.min(
    activeQuestionIndex,
    Math.max(yesNoQuestions.length - 1, 0),
  );
  const activeQuestion = yesNoQuestions[safeQuestionIndex];
  usePreloadAudioUrls(isPreview ? [] : [activeQuestion?.audio]);
  const isFirstQuestion = safeQuestionIndex === 0;
  const isLastQuestion = safeQuestionIndex >= yesNoQuestions.length - 1;
  const currentQuestionAnswered = Boolean(
    activeQuestion && answerByQuestionId[activeQuestion.question_id],
  );

  const isComplete =
    yesNoQuestions.length > 0 &&
    yesNoQuestions.every((question) =>
      Boolean(answerByQuestionId[question.question_id]),
    );
  const hasQuestionContent = yesNoQuestions.some(
    (question) =>
      Boolean(question.prompt?.trim()) ||
      Boolean(question.image?.trim()) ||
      Boolean(question.audio?.trim()) ||
      Boolean(question.options?.length) ||
      Boolean(question.optionA?.trim()) ||
      Boolean(question.optionB?.trim()),
  );
  const canAdvanceWithoutContent =
    yesNoQuestions.length === 0 || !hasQuestionContent;

  useEffect(() => {
    onStepReadyChange?.(isComplete || canAdvanceWithoutContent);
  }, [canAdvanceWithoutContent, isComplete, onStepReadyChange]);

  useEffect(() => {
    if (isPreview) {
      return;
    }
    if (!activeQuestion?.question_id) {
      return;
    }
    const frame = requestAnimationFrame(() => {
      promptAudioRef.current?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [activeQuestion?.question_id, isPreview]);

  if (isLoading) {
    return null;
  }

  if (!assessment?.part) {
    return (
      <Typography>No part content available. You can continue.</Typography>
    );
  }
  if (!activeQuestion) {
    return (
      <Typography>
        No yes/no questions found for this part. You can continue.
      </Typography>
    );
  }

  const options = activeQuestion
    ? extractOptions(activeQuestion)
    : { a: "Yes", b: "No" };
  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      gap={3}
      sx={{ width: "100%", maxWidth: 1400, mx: "auto" }}
    >
      <Card
        sx={{
          flex: "60%",
          display: "flex",
          flexDirection: "column",
          height: { xs: "40vh", md: "50vh" },
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {activeQuestion.image ? (
          <Box
            component="img"
            src={activeQuestion.image}
            alt={`Yes/No prompt ${safeQuestionIndex + 1}`}
            sx={{ height: "100%", width: "100%", objectFit: "contain" }}
          />
        ) : null}
      </Card>
      <Card
        sx={{
          flex: "40%",
          display: "flex",
          flexDirection: "column",
          height: { xs: "40vh", md: "50vh" },
          minHeight: 0,
          overflow: "hidden",
        }}
        variant={"filled"}
      >
        <CardContent
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            p: 3,
          }}
        >
          <Stack spacing={2} alignItems={"flex-start"} sx={{ minWidth: 0 }}>
            <Stack direction={"row"} alignItems={"center"} spacing={2}>
              <InstructionAudioButton
                audioUrl={assessment?.yesNoQuestionAudio}
                label="Play yes/no question instructions audio"
              />
              <Typography variant={"body1"} sx={{ fontWeight: 800 }}>
                Instructions:
              </Typography>
            </Stack>
          </Stack>
          <Stack direction="column" spacing={1}>
            <Typography sx={{ fontWeight: 600 }}>
              {activeQuestion.prompt || `Question ${safeQuestionIndex + 1}`}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              {`Question ${safeQuestionIndex + 1} of ${yesNoQuestions.length}`}
            </Typography>
            <AudioPlayer
              src={activeQuestion.audio ?? ""}
              audioRef={promptAudioRef}
              isPreview={isPreview}
            />
          </Stack>
          {activeQuestion ? (
            <Stack
              direction={"column"}
              justifyContent={"space-between"}
              height={"100%"}
            >
              <RadioButtonsGroup
                ariaLabel={`yes-no-${activeQuestion.question_id}`}
                allowKeyboardDeselect
                value={answerByQuestionId[activeQuestion.question_id] ?? ""}
                options={[
                  { value: "A", label: options.a },
                  { value: "B", label: options.b },
                ]}
                onChange={(nextRawValue) => {
                  if (nextRawValue === "A" || nextRawValue === "B") {
                    setAnswerByQuestionId((current) => ({
                      ...current,
                      [activeQuestion.question_id]: nextRawValue,
                    }));
                    void onSaveAnswer?.(
                      activeQuestion.question_id,
                      nextRawValue,
                    );
                    return;
                  }
                  setAnswerByQuestionId((current) => {
                    const next = { ...current };
                    delete next[activeQuestion.question_id];
                    return next;
                  });
                  void onSaveAnswer?.(activeQuestion.question_id, nextRawValue);
                }}
              />

              <Stack
                direction="row"
                spacing={1.5}
                justifyContent={"space-between"}
              >
                <Button
                  size="medium"
                  variant="outlined"
                  disabled={isFirstQuestion}
                  onClick={() =>
                    setActiveQuestionIndex((previous) =>
                      Math.max(previous - 1, 0),
                    )
                  }
                >
                  Previous
                </Button>
                {!isComplete ? (
                  <Button
                    size="medium"
                    variant="contained"
                    disabled={isLastQuestion || !currentQuestionAnswered}
                    onClick={() =>
                      setActiveQuestionIndex((previous) =>
                        Math.min(previous + 1, yesNoQuestions.length - 1),
                      )
                    }
                  >
                    Next
                  </Button>
                ) : null}
              </Stack>
            </Stack>
          ) : null}
        </CardContent>
      </Card>
    </Stack>
  );
}
